import {
  DataWarehouseConnection,
  WorkflowCodeType,
  WorkflowTableSchemas,
  Workflow as WorkflowEngine
} from '@cloud-native/workflows-engine'
import { WorkflowNodeStatus, WorkflowsExecutionStatus } from '../../../enums'
import { JobStatus } from '../../../../jobs/types'
import { Workflow, WorkflowNodeModel } from '../../../types'
import { CONTROL_COMPONENTS } from 'features/workflows/constants'

export async function getCompleteNodeDetails(
  workflowEngine: WorkflowEngine,
  dataWarehouseConnection: DataWarehouseConnection,
  workflowState: WorkflowsExecutionStatus,
  error: JobStatus['error'] | null,
  failedComponent: string | null,
  workflow: Workflow,
  existingOutputTables: string[] | null,
  schemas?: WorkflowTableSchemas
) {
  const workflowParams = workflowEngine.componentValues
  const orderedComponents: Array<string> = workflowEngine.getComponentsInRunningOrder()
  const nodeDetails: Array<WorkflowNodeModel> = []

  const failed = error && Object.keys(error).length
  const errorMessage = error?.msg
  let state: WorkflowNodeStatus = WorkflowNodeStatus.Success
  let nodeError: string | undefined
  let componentIndex = 1
  for (const componentName of orderedComponents) {
    const component = workflowEngine.components[componentName]
    const componentParams = workflowParams[componentName]
    const componentResolvedParams = workflowEngine.resolveParameterValues({
      connection: dataWarehouseConnection,
      codeType: WorkflowCodeType.Full
    })[componentName]
    const nextComponent = orderedComponents[componentIndex++]
    const nextComponentName = workflowEngine.components[nextComponent]?.metadata?.name
    const isControlComponent = CONTROL_COMPONENTS.includes(component?.metadata?.name)

    let outputs: Record<string, string> = {}
    try {
      const errors = component.checkInputValues({
        params: componentParams,
        resolvedParamValues: componentResolvedParams,
        connection: dataWarehouseConnection,
        dryRun: false,
        schemas,
        variables: workflow.config.variables
      })
      if (errors?.length) {
        state =
          workflowState === WorkflowsExecutionStatus.Running
            ? WorkflowNodeStatus.Running
            : WorkflowNodeStatus.Unexecutable
        nodeError = errors?.[0]?.message
      } else if (workflowState === WorkflowsExecutionStatus.Running) {
        state = WorkflowNodeStatus.Running
      } else if (failed && [null, undefined, componentName].includes(failedComponent)) {
        state = WorkflowNodeStatus.Failure
        nodeError = componentName === failedComponent ? errorMessage : undefined
      } else if (workflowState === WorkflowsExecutionStatus.Unexecuted) {
        state = WorkflowNodeStatus.Unexecuted
        nodeError = undefined
      } else {
        state = WorkflowNodeStatus.Success
        nodeError = undefined
      }
      if (!errors?.length) {
        outputs = await component.getOutputTables({
          workflow: workflowEngine,
          params: componentResolvedParams,
          connection: dataWarehouseConnection,
          codeType: workflow?.config?.useCache ? WorkflowCodeType.Full : WorkflowCodeType.FullNoCache,
          componentName
        })
      }
    } catch (e) {
      state =
        workflowState === WorkflowsExecutionStatus.Running
          ? WorkflowNodeStatus.Running
          : WorkflowNodeStatus.Unexecutable
      nodeError = `${e}`
    }

    const outputTables = Object.keys(outputs).map((outputName) => ({
      name: outputName,
      tableFqn: outputs[outputName]
    }))

    // Update each node status by checking the output tables, except control components
    if (existingOutputTables && !isControlComponent && !outputTablesExists(outputTables, existingOutputTables)) {
      // If none of the output tables exists, update the state
      if (state === WorkflowNodeStatus.Success) {
        // Sucess node with no output table will be marked as Unexecuted, except if the next
        // node is a "native.successerror", in which case, it will be marked as Failure.
        if (nextComponentName !== 'native.successerror') {
          state = WorkflowNodeStatus.Unexecuted
        } else {
          state = WorkflowNodeStatus.Failure
        }
      }
    }

    nodeDetails.push({
      name: componentName,
      title: component.metadata.title,
      error: nodeError,
      state,
      outputTables
    })
  }
  return nodeDetails
}

function outputTablesExists(outputTables, existingOutputTables) {
  return outputTables?.some((table) => {
    const tableName = table.tableFqn?.split('.')?.[2]
    return existingOutputTables.includes(tableName)
  })
}
